/**
 * Helpers for reducing/transforming data.
 */
import Product from 'redux/models/Product';
import { USER_FACING_COPY } from '../utils';

const INVNTRY_DLVRY_INFO = USER_FACING_COPY.inventoryAndDeliveryInfo;

export function reduceProduct ({
  product,
  allLikeStatus,
  allRetailerPromos
}) {
  const prod = product;
  const isExactMatch = prod.isExactMatch();
  const isPromoExcluded = prod.isPromoExcluded();
  const isStandIn = prod.isStandIn();

  let status = '';
  let estimatedDeliveryMsg = '';
  let specialMessaging = '';
  if (!isExactMatch && !isStandIn) {
    const hasShippingQuote = prod.shippingQuote();
    switch (true) {
      case prod.isMadeToOrder():
        status = 'MADE_TO_ORDER';
        estimatedDeliveryMsg = hasShippingQuote ? INVNTRY_DLVRY_INFO.showEstimatedDelivery({ product: prod }) : '';
        specialMessaging = prod.specialMessaging() || '';
        break;
      case prod.isDiscontinued():
        status = 'DISCONTINUED';
        estimatedDeliveryMsg = USER_FACING_COPY.outOfStock;
        specialMessaging = USER_FACING_COPY.outOfStockMessaging;
        break;
      case prod.isBackOrdered():
        status = 'BACKORDERED';
        estimatedDeliveryMsg = INVNTRY_DLVRY_INFO.backOrderedText(prod.getEstimatedRestockDate());
        specialMessaging = prod.specialMessaging() || '';
        break;
      case prod.isOutOfStock():
        status = 'OUT_OF_STOCK';
        estimatedDeliveryMsg = USER_FACING_COPY.outOfStock;
        specialMessaging = USER_FACING_COPY.outOfStockMessaging;
        break;
      default:
        status = 'IN_STOCK';
        estimatedDeliveryMsg = hasShippingQuote ? INVNTRY_DLVRY_INFO.showEstimatedDelivery({ product: prod }) : '';
        specialMessaging = prod.specialMessaging() || '';
        break;
    }
  }

  let badge = '';
  switch (true) {
    case isExactMatch:
      badge = 'Exact match';
      break;
    case isStandIn:
      badge = 'Stand-in';
      break;
    case prod.isDiscounted():
      badge = 'Sale';
      break;
    case prod.isNew():
      badge = 'New';
      break;
    default:
      break;
  }

  const description = (isExactMatch || isStandIn)
    ? ''
    : (prod.descriptionWithoutLinks() || '');

  const images = (prod.images() || []).map(i => i.filename);
  const heroImg = images[0] || '';

  const addlInfo = prod.additionalInfo() || {};
  const assemblyRequired = addlInfo.construction || '';
  const careInstructions = addlInfo.careInstruction || '';
  const color = addlInfo.color || prod.color();
  const material = addlInfo.fabric || prod.material();
  const finish = addlInfo.finish || '';
  const originCountry = addlInfo.originCountry || '';

  const shippingInfo = (isExactMatch || isStandIn)
    ? ''
    : (prod.shippingPolicy() || '');

  return {
    id: `${prod.id()}`,
    availabilityV2: {
      estimatedDeliveryMsg,
      specialMessaging,
      status
    },
    badge,
    description,
    externalId: prod.externalId() || '',
    heroImg,
    images,
    isSellable: prod.isSellable(),
    is2dOnlyProduct: !prod.has3dAssets(),
    isMaterial: prod.isMaterial(),
    isPromoExcluded,
    liked: allLikeStatus[prod.id()] || 0,
    mainType: product.mainType(),
    quantityMultiple: product.getQuantityMultiple(),
    name: prod.name() || '',
    physicalDetails: {
      assemblyRequired,
      careInstructions,
      color,
      depth: prod.depth() || 0,
      finish,
      height: prod.height() || 0,
      material,
      originCountry,
      width: prod.width() || 0
    },
    promos: allRetailerPromos[prod.retailer() || ''] || [],
    source: prod.retailer() || '',
    regularPrice: prod.regularPrice(),
    salePrice: (prod.currentPrice() < prod.regularPrice()) ? prod.currentPrice() : 0,
    shippingInfo,
    variantsHash: prod.variantHash() || null,
    variantAttributes: prod.variantAttributes()
  };
}

export function reduceAtcStatus ({
  atcLoading,
  activeProduct,
  isBuyFromRetailer,
  shoppingCart
}) {
  switch (true) {
    case shoppingCart && shoppingCart.isProductInCart(activeProduct):
      return 'added-to-cart';
    case isBuyFromRetailer:
      return 'buy-from-retailer';
    case atcLoading:
      return 'adding-to-cart';
    default:
      return 'not-in-cart';
  }
}

export function reduceVariants ({
  activeProduct,
  initialProduct,
  allLikeStatus,
  allRetailerPromos,
  variantsByAttribute,
  variantsLoading,
  variantProducts
}) {
  let variantsState = { color: '', dimensions: '' };

  if (
    !activeProduct.variantHash() ||
    variantsLoading ||
    !variantProducts ||
    variantProducts.length === 0
  ) {
    return {
      variants: null,
      variantsState
    };
  }

  // Reduce the variant products.
  const variants = (variantProducts || []).map(prod => {
    return reduceProduct({
      allLikeStatus,
      allRetailerPromos,
      product: prod
    });
  });

  // Construct our full 2-D matrix of possible color and dimensions combinations.
  // Monarch PDM uses a full grid for the variant color and dimension
  // attributes while PDP uses a sparse grid.
  let colorVariants = [];
  if (variantsByAttribute) {
    const { color, dimensions } = variantsByAttribute;
    const allColors = Object.keys(color || {});
    const allDims = Object.keys(dimensions || {});

    if (allColors.length > 0 && allDims.length > 0) {
      // Add the initial/active product into our variants.
      const initial = activeProduct;
      if (initial) {
        variants.push(reduceProduct({
          allLikeStatus,
          allRetailerPromos,
          product: initial
        }));
      }

      // Initialize our full 2-D matrix of possible color and dimensions combinations.
      // Every color gets the full set of possible dimensions due to UI requirements
      const variantsMatrix = {};
      allColors.forEach(color => {
        variantsMatrix[color.trim()] = {};
        allDims.forEach(dimensions => {
          variantsMatrix[color.trim()][dimensions.trim()] = {
            availabilityStatus: null,
            dimensions,
            image: null,
            productId: null
          };
        });
      });

      // Loop over all variants and populate the variantsMatrix
      variants.forEach(variant => {
        const { status: availabilityStatus } = variant.availabilityV2;
        const { color, dimensions } = variant.variantAttributes;
        if (dimensions && variantsMatrix[color.trim()]) {
          variantsMatrix[color.trim()][dimensions.trim()] = {
            availabilityStatus,
            dimensions: dimensions.trim(),
            image: variant.images[0] || '',
            productId: variant.id
          };
        }
      });

      // Finally, reduce the variants matrix into our final data structure.
      colorVariants = allColors.map(color => ({
        color,
        dimsTable: allDims.map(dimensions => variantsMatrix[color][dimensions])
      }));
    }
  }

  const variantsData = {
    variantsHash: activeProduct.variantHash(),
    colorVariants,
    variants
  };

  const { color, dimensions } = activeProduct.variantAttributes() || {};
  if (dimensions) {
    variantsState = { color: color.trim(), dimensions: dimensions.trim() };
  }

  return {
    variants: variantsData,
    variantsState
  };
}

export function reduceAddons ({
  addToCartProductId,
  allLikeStatus,
  allRetailerPromos,
  productAddOns,
  shoppingCart
}) {
  return (productAddOns || []).map(prod => {
    const whProduct = Product.createProductFromWarehouseProductResponse({ whProduct: prod });
    const isInCart = shoppingCart && shoppingCart.isProductInCart(whProduct);
    const atcStatus = isInCart
      ? 'added-to-cart'
      : (prod.id === addToCartProductId ? 'adding-to-cart' : 'not-in-cart');

    return {
      atcStatus,
      ...reduceProduct({
        allLikeStatus,
        allRetailerPromos,
        product: whProduct
      }),
      whProduct // we will need access to the whProduct in the addon event handlers
    };
  });
}

export function reduceSimilarProducts ({
  allLikeStatus,
  allRetailerPromos,
  alternativeProducts
}) {
  return (alternativeProducts || []).map(prod => {
    return reduceProduct({
      allLikeStatus,
      allRetailerPromos,
      product: prod
    });
  });
}

export function reduceSwapStatus ({
  fromCart,
  product,
  swapAllowed,
  swapInProductId,
  swapInProgress
}) {
  switch (true) {
    case fromCart: return 'unavailable';
    case product.is2dOnlyProduct: return 'unavailable';
    case (swapAllowed && swapInProgress && swapInProductId === Number(product.id)): return 'in-progress-active';
    case (swapAllowed && swapInProgress): return 'in-progress';
    case swapAllowed: return 'available';
    default: return 'unavailable';
  }
}
